// docs for router https://github.com/thepassle/app-tools/blob/master/router/README.md

import { html } from 'lit';

if (!('URLPattern' in globalThis)) {
  await import('urlpattern-polyfill');
}

import { Router } from '@thepassle/app-tools/router.js';

import './pages/app-home.js';
import './pages/app-profile/app-profile.js';
import './pages/app-about/app-about.js';
import './pages/app-privacy/app-privacy.js';
import './pages/app-terms/app-terms.js';
import './help/help-index-page.js';
import './help/help-topic-page.js';
import './pages/order-page.js';
import './pages/sales-new-page.js';
import './pages/sales-history-page.js';
import './pages/sales-reports-page.js';
import './pages/inventory-page.js';
import './pages/products-page.js';
import './pages/categories-page.js';
import './pages/settings-page.js';
import './pages/order-track-page.js';
import './pages/shop-create-page.js';
import './pages/shop-sample-page.js';
import './pages/shop-select-page.js';
import './pages/shop-settings-page.js';
import './pages/stock-list-page.js';
import './pages/stock-edit-page.js';
import './pages/stock-locations-page.js';
import './pages/stocktake-list-page.js';
import './pages/stocktake-edit-page.js';
import './pages/staff-page.js';
import './pages/suppliers-list-page.js';
import './pages/supplier-edit-page.js';
import './pages/purchase-orders-list-page.js';
import './pages/purchase-order-edit-page.js';
import './pages/deliveries-list-page.js';
import './pages/delivery-edit-page.js';
import './pages/product-edit-page.js';
import './pages/active-orders-page.js';
import './pages/active-order-detail-page.js';
import './pages/dashboard-page.js';
import './pages/data-table-lab-page.js';
import './pages/cash-drawer-page.js';
import './pages/cash-sessions-page.js';
import './pages/cash-z-report-page.js';
import './pages/shop-days-page.js';
import './pages/daily-z-report-page.js';
import './pages/app-login/app-login.js'; // import the login page
import './pages/app-login/app-google-callback.js'; // Google OAuth redirect landing
import './pages/app-welcome/app-welcome.js';
import './pages/app-mode-picker/app-mode-picker.js';
import './pages/customer-home/customer-home.js';
import './pages/join-shop-page/join-shop-page.js';

const baseURL: string = import.meta.env.BASE_URL;

export const router = new Router({
  routes: [
    {
      path: resolveRouterPath(),
      title: 'Home',
      render: () => html`<app-home></app-home>`,
    },
    {
      path: resolveRouterPath('about'),
      title: 'About',

      render: () => html`<app-about></app-about>`,
    },
    {
      // Anonymous-accessible. Required for Google OAuth consent
      // screen review, which crawls the privacy URL directly.
      path: resolveRouterPath('privacy'),
      title: 'Privacy Policy',
      render: () => html`<app-privacy></app-privacy>`,
    },
    {
      // Anonymous-accessible. Sibling of /privacy.
      path: resolveRouterPath('terms'),
      title: 'Terms of Use',
      render: () => html`<app-terms></app-terms>`,
    },
    {
      // In-app manual: table of contents.
      path: resolveRouterPath('help'),
      title: 'Help',
      render: () => html`<help-index-page></help-index-page>`,
    },
    {
      // In-app manual: a single topic. Slug passed as a property so a param
      // change re-renders the page.
      path: resolveRouterPath('help/:slug'),
      title: 'Help',
      render: () =>
        html`<help-topic-page
          .slug=${
            (
              router.context as unknown as
                { params?: { slug?: string } } | undefined
            )?.params?.slug ?? ''
          }
        ></help-topic-page>`,
    },
    {
      path: resolveRouterPath('user/profile'),
      title: 'Profile',

      render: () => html`<app-profile></app-profile>`,
    },
    {
      path: resolveRouterPath('order'),
      title: 'Order',

      render: () => html`<order-page></order-page>`,
    },
    {
      path: resolveRouterPath('sales/new'),
      title: 'New Sales',

      render: () => html`<sales-new-page></sales-new-page>`,
    },
    {
      path: resolveRouterPath('sales/history'),
      title: 'Sales History',

      render: () => html`<sales-history-page></sales-history-page>`,
    },
    {
      path: resolveRouterPath('sales/reports'),
      title: 'Sales Reports',

      render: () => html`<sales-reports-page></sales-reports-page>`,
    },
    {
      path: resolveRouterPath('dashboard'),
      title: 'Dashboard',

      render: () => html`<dashboard-page></dashboard-page>`,
    },
    {
      path: resolveRouterPath('inventory'),
      title: 'Inventory',

      render: () => html`<inventory-page></inventory-page>`,
    },
    {
      // B1 — operator-facing cash drawer page (open / close session,
      // see current drawer balance). Gated client-side by
      // subscription.allowsCashManagement; the server is the load-bearing
      // gate.
      path: resolveRouterPath('cash'),
      title: 'Cash Drawer',
      render: () => html`<cash-drawer-page></cash-drawer-page>`,
    },
    {
      path: resolveRouterPath('cash/sessions'),
      title: 'Cash Sessions',
      render: () => html`<cash-sessions-page></cash-sessions-page>`,
    },
    {
      path: resolveRouterPath('cash/sessions/:id'),
      title: 'Z-Report',
      render: () => html`<cash-z-report-page></cash-z-report-page>`,
    },
    {
      path: resolveRouterPath('products'),
      title: 'Products',
      render: () => html`<products-page></products-page>`,
    },
    {
      path: resolveRouterPath('categories'),
      title: 'Categories',
      render: () => html`<categories-page></categories-page>`,
    },
    {
      path: resolveRouterPath('products/new'),
      title: 'New product',
      render: () => html`<product-edit-page></product-edit-page>`,
    },
    {
      path: resolveRouterPath('products/:id'),
      title: 'Edit product',
      render: () => html`<product-edit-page></product-edit-page>`,
    },
    {
      path: resolveRouterPath('settings'),
      title: 'Settings',

      render: () => html`<settings-page></settings-page>`,
    },
    {
      path: resolveRouterPath('welcome'),
      title: 'Welcome',
      render: () => html`<app-welcome></app-welcome>`,
    },
    {
      path: resolveRouterPath('login'),
      title: 'Login',
      render: () => html`<app-login></app-login>`,
    },
    {
      path: resolveRouterPath('auth/google/callback'),
      title: 'Signing in…',
      render: () => html`<app-google-callback></app-google-callback>`,
    },
    {
      path: resolveRouterPath('shop/create'),
      title: 'Create Shop',
      render: () => html`<shop-create-page></shop-create-page>`,
    },
    {
      path: resolveRouterPath('shop/sample'),
      title: 'Try with sample data',
      render: () => html`<shop-sample-page></shop-sample-page>`,
    },
    {
      // Phase Y.B1 — public order tracking. Anonymous, no shell.
      // Token is read from the path by the page itself.
      path: resolveRouterPath('track/:token'),
      title: 'Order tracking',
      render: () => html`<order-track-page></order-track-page>`,
    },
    {
      path: resolveRouterPath('shop/select'),
      title: 'Select Shop',
      render: () => html`<shop-select-page></shop-select-page>`,
    },
    {
      path: resolveRouterPath('shop/settings'),
      title: 'Shop Settings',
      render: () => html`<shop-settings-page></shop-settings-page>`,
    },
    {
      path: resolveRouterPath('shop/days'),
      title: 'Daily Z-reports',
      render: () => html`<shop-days-page></shop-days-page>`,
    },
    {
      path: resolveRouterPath('shop/days/:id/z-report'),
      title: 'Daily Z-Report',
      render: () => html`<daily-z-report-page></daily-z-report-page>`,
    },
    {
      path: resolveRouterPath('orders/active'),
      title: 'Active Orders',
      render: () => html`<active-orders-page></active-orders-page>`,
    },
    {
      path: resolveRouterPath('orders/active/:id'),
      title: 'Order details',
      render: () => html`<active-order-detail-page></active-order-detail-page>`,
    },
    {
      path: resolveRouterPath('stock'),
      title: 'Stock',
      render: () => html`<stock-list-page></stock-list-page>`,
    },
    {
      path: resolveRouterPath('stock/locations'),
      title: 'Stock locations',
      render: () => html`<stock-locations-page></stock-locations-page>`,
    },
    {
      path: resolveRouterPath('stock/new'),
      title: 'New stock item',
      render: () => html`<stock-edit-page></stock-edit-page>`,
    },
    {
      path: resolveRouterPath('stock/:id'),
      title: 'Edit stock item',
      render: () => html`<stock-edit-page></stock-edit-page>`,
    },
    {
      path: resolveRouterPath('stocktake'),
      title: 'Stock takes',
      render: () => html`<stocktake-list-page></stocktake-list-page>`,
    },
    {
      path: resolveRouterPath('stocktake/:id'),
      title: 'Stock take',
      render: () => html`<stocktake-edit-page></stocktake-edit-page>`,
    },
    {
      path: resolveRouterPath('staff'),
      title: 'Staff',
      render: () => html`<staff-page></staff-page>`,
    },
    {
      path: resolveRouterPath('suppliers'),
      title: 'Suppliers',
      render: () => html`<suppliers-list-page></suppliers-list-page>`,
    },
    {
      path: resolveRouterPath('suppliers/new'),
      title: 'New supplier',
      render: () => html`<supplier-edit-page></supplier-edit-page>`,
    },
    {
      path: resolveRouterPath('suppliers/:id'),
      title: 'Edit supplier',
      render: () => html`<supplier-edit-page></supplier-edit-page>`,
    },
    {
      path: resolveRouterPath('purchase-orders'),
      title: 'Purchase orders',
      render: () =>
        html`<purchase-orders-list-page></purchase-orders-list-page>`,
    },
    {
      path: resolveRouterPath('purchase-orders/new'),
      title: 'New purchase order',
      render: () => html`<purchase-order-edit-page></purchase-order-edit-page>`,
    },
    {
      path: resolveRouterPath('purchase-orders/:id'),
      title: 'Purchase order',
      render: () => html`<purchase-order-edit-page></purchase-order-edit-page>`,
    },
    {
      path: resolveRouterPath('deliveries'),
      title: 'Deliveries',
      render: () => html`<deliveries-list-page></deliveries-list-page>`,
    },
    {
      path: resolveRouterPath('deliveries/new'),
      title: 'New delivery',
      render: () => html`<delivery-edit-page></delivery-edit-page>`,
    },
    {
      path: resolveRouterPath('deliveries/:id'),
      title: 'Delivery',
      render: () => html`<delivery-edit-page></delivery-edit-page>`,
    },
    {
      path: resolveRouterPath('lab/data-table'),
      title: 'Data table — lab',
      render: () => html`<data-table-lab-page></data-table-lab-page>`,
    },
    {
      path: resolveRouterPath('mode-picker'),
      title: 'Choose your mode',
      render: () => html`<app-mode-picker></app-mode-picker>`,
    },
    {
      path: resolveRouterPath('customer'),
      title: 'My memberships',
      render: () => html`<customer-home></customer-home>`,
    },
    {
      // Public deep link from a scanned shop QR. The page resolves
      // the code → shop and walks the visitor through auth + enrol +
      // join. Anonymous-accessible — auth is handled inside the page.
      path: resolveRouterPath('join/:code'),
      title: 'Join shop',
      render: () => html`<join-shop-page></join-shop-page>`,
    },
  ],
});

// This function will resolve a path with whatever Base URL was passed to the vite build process.
// Use of this function throughout the starter is not required, but highly recommended, especially if you plan to use GitHub Pages to deploy.
// If no arg is passed to this function, it will return the base URL.

export function resolveRouterPath(unresolvedPath?: string) {
  var resolvedPath = baseURL;
  if (unresolvedPath) {
    resolvedPath = resolvedPath + unresolvedPath;
  }

  return resolvedPath;
}
